import { z } from 'zod';
import {
  CarrierPriceListsResponseSchema,
  CarrierPriceListSchema,
  CarrierWarehousesResponseSchema,
  CarrierWarehouseSchema,
  CarrierSchema,
  GetCarriersResponseSchema,
  StorageDownloadUrlResponseSchema,
  type Carrier,
  type CarrierPriceList,
  type CarrierPriceListCatalogItem,
  type CarrierPriceListsQuery,
  type CarrierPriceListsResponse,
  type CarrierWarehouse,
  type CarrierWarehousesQuery,
  type CarrierWarehousesResponse,
  type CreateCarrierInput,
  type GetCarriersQuery,
  type GetCarriersResponse,
  type PatchCarrierInput,
  type StorageDownloadUrlResponse,
} from '@magimp/shared';
import { request } from '../client.js';
import { withQuery } from './_query.js';

export function listCarriers(query: Partial<GetCarriersQuery> = {}): Promise<GetCarriersResponse> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.active !== undefined) params.set('active', String(query.active));
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return request<GetCarriersResponse>(
    withQuery('/carriers', qs),
    { method: 'GET' },
    {},
    GetCarriersResponseSchema,
  );
}

export function fetchCarrierCatalogItem(id: string): Promise<Carrier> {
  return request<Carrier>(
    `/carriers/${encodeURIComponent(id)}`,
    { method: 'GET' },
    {},
    CarrierSchema,
  );
}

export function createCarrier(body: CreateCarrierInput): Promise<Carrier> {
  return request<Carrier>(
    '/carriers',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierSchema,
  );
}

export function patchCarrierCatalog(id: string, body: PatchCarrierInput): Promise<Carrier> {
  return request<Carrier>(
    `/carriers/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierSchema,
  );
}

export function deactivateCarrier(id: string): Promise<Carrier> {
  return request<Carrier>(
    `/carriers/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    {},
    CarrierSchema,
  );
}

export function activateCarrier(id: string): Promise<Carrier> {
  return request<Carrier>(
    `/carriers/${encodeURIComponent(id)}/activate`,
    { method: 'POST' },
    {},
    CarrierSchema,
  );
}

export type CarrierWarehouseBody = Pick<CarrierWarehouse, 'name' | 'city' | 'description'>;
export type CarrierPriceListBody = Pick<
  CarrierPriceList,
  'name' | 'imageObjectKey' | 'fileContentType'
>;

export function createCarrierWarehouse(
  carrierId: string,
  body: CarrierWarehouseBody,
): Promise<CarrierWarehouse> {
  return request<CarrierWarehouse>(
    `/carriers/${encodeURIComponent(carrierId)}/warehouses`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierWarehouseSchema,
  );
}

export function patchCarrierWarehouse(
  carrierId: string,
  warehouseId: string,
  body: Partial<CarrierWarehouseBody>,
): Promise<CarrierWarehouse> {
  return request<CarrierWarehouse>(
    `/carriers/${encodeURIComponent(carrierId)}/warehouses/${encodeURIComponent(warehouseId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierWarehouseSchema,
  );
}

export function deleteCarrierWarehouse(carrierId: string, warehouseId: string): Promise<void> {
  return request<void>(
    `/carriers/${encodeURIComponent(carrierId)}/warehouses/${encodeURIComponent(warehouseId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

export function createCarrierPriceList(
  carrierId: string,
  body: CarrierPriceListBody,
): Promise<CarrierPriceList> {
  return request<CarrierPriceList>(
    `/carriers/${encodeURIComponent(carrierId)}/price-lists`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierPriceListSchema,
  );
}

export function patchCarrierPriceList(
  carrierId: string,
  priceListId: string,
  body: Partial<CarrierPriceListBody>,
): Promise<CarrierPriceList> {
  return request<CarrierPriceList>(
    `/carriers/${encodeURIComponent(carrierId)}/price-lists/${encodeURIComponent(priceListId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CarrierPriceListSchema,
  );
}

export function deleteCarrierPriceList(carrierId: string, priceListId: string): Promise<void> {
  return request<void>(
    `/carriers/${encodeURIComponent(carrierId)}/price-lists/${encodeURIComponent(priceListId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

export function listWarehouses(
  query: Partial<CarrierWarehousesQuery> = {},
): Promise<CarrierWarehousesResponse> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return request<CarrierWarehousesResponse>(
    withQuery('/warehouses', params.toString()),
    { method: 'GET' },
    {},
    CarrierWarehousesResponseSchema,
  );
}

export function listPriceLists(
  query: Partial<CarrierPriceListsQuery> = {},
): Promise<CarrierPriceListsResponse> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return request<CarrierPriceListsResponse>(
    withQuery('/price-lists', params.toString()),
    { method: 'GET' },
    {},
    CarrierPriceListsResponseSchema,
  );
}

export function requestPriceListImageDownloadUrl(
  priceListId: string,
): Promise<StorageDownloadUrlResponse> {
  return request<StorageDownloadUrlResponse>(
    `/price-lists/${encodeURIComponent(priceListId)}/image-download-url`,
    { method: 'POST' },
    {},
    StorageDownloadUrlResponseSchema,
  );
}

// Re-export Carrier so call sites can import the type from this module
// without crossing into @magimp/shared for one symbol.
export type { Carrier, CarrierPriceListCatalogItem };
